import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Container, Grid2, Typography } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableOptions,
  useMaterialReactTable
} from "material-react-table";
import ExcelJS from "exceljs";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { InfoIconWithTooltip } from "../../../components/Title/InfoIcon";
import { useRemoteFetchCompanies } from "../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { useRemoteResetPassword } from "../../../hooks/Remote/User/UserInfo/useRemoteResetPassword";
import { useRemoteFetchCompanyUnits } from "../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Stakeholder } from "../../FloorManager/typings/permission.entity";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { UserManagementRow } from "./typings/user-management.type";
import { UserManagementTableRowActions } from "./user-management-row-actions";
import { UserManagementTopToolbar } from "./user-management-top-toolbar";
import { resetPasswordCell, resetPasswordHeader } from "./user-management-resetPassword.partial";
import { useRemoteCreateOrUpdateUser } from "../../../hooks/Remote/User/useRemoteCreateOrUpdateUser";
import {
  handleRowInputChange,
  mapAddRowValueToUserDto,
  mapUpdRowValueToUserDto,
  updateUserCompanyDepartment
} from "./utils/user-management.functions";
import { validateUserManagementRow } from "./utils/validateUserManagementRow.function";
import { useRemoteDeleteUsers } from "../../../hooks/Remote/User/useRemoteDeleteUsers";
import {
  userRowCellCompanyId,
  userRowCellDepartmentId,
  userRowCellPhotoAvatar,
  userRowCellActive,
  userRowBooleanCell
} from "./user-management.partial";
import { commonLocalization } from "../../../functions/tableLocalization";
import TableDeleteConfirmDialog from "../../../components/Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { exportExcelByDocument } from "../../../components/FacilityManager/Functions/exportExcelByDocument";
import { SlidePanelCreateEditUserManagement } from "./user-managment-create-edit-content.partial";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";

const UserManagement = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { userRoles } = useSelector((state: RootState) => state.login);

  const [users, setUsers] = useState<UserManagementRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<string[] | null>(null);

  const {
    data: userData,
    refetch: refetchUserData,
    isLoading: isUserDataLoading,
    isFetching: isUserDataFetching
  } = useRemoteFetchUserInfoForAllUsers();

  const { data: companyData } = useRemoteFetchCompanies();
  const { data: departmentData } = useRemoteFetchCompanyUnits(Stakeholder.CostCenterUnit);
  const { mutate: resetPassword } = useRemoteResetPassword();

  const { mutateAsync: mutateUser, status: mutateUserStatus } = useRemoteCreateOrUpdateUser();
  const { mutateAsync: deleteUser, status: deleteUserStatus } = useRemoteDeleteUsers();

  const nullDepartment = [
    {
      id: "-1",
      name: t("No Department"),
      companyId: 0,
      description: "",
      active: false,
      isDepartment: false,
      responsibleEmail: ""
    }
  ];

  /** deletes users */
  async function handleRowDelete(rowEmails: string[]) {
    deleteUser({ emails: rowEmails })
      .then(() => enqueueSnackbar(t("UserInfoDeleteSuccess"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("UserInfoDeleteFail"), { variant: "error" }));
    table.resetRowSelection();
  }

  /** adds an user */
  const handleRowAdd: MRT_TableOptions<UserManagementRow>["onCreatingRowSave"] = async ({
    row,
    values,
    table
  }) => {
    const updated = updateUserCompanyDepartment(values, companyData, departmentData);
    const newValidationErrors = validateUserManagementRow(updated, row.original, t);

    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }
    setValidationErrors({});

    const newVal = mapAddRowValueToUserDto(updated);

    await mutateUser([newVal])
      .then(() => enqueueSnackbar(t("UserInfoCreateSuccess"), { variant: "success" }))
      .catch(e => {
        if (e.response.status === 500) {
          return enqueueSnackbar(t("UserInfoUpdateSupervisorFail"), { variant: "error" });
        }
        //checking for existing rootCause property for the case that there is a 409 statuscode but no keycloak error
        if (e.response.status === 409 && e.response.data.rootCause) {
          const errorMessage = JSON.parse(e.response.data.rootCause.split("error: ")[1])
            .responseData.errorMessage;
          if (errorMessage === "User exists with same email") {
            return enqueueSnackbar(t("User already exists"), { variant: "error" });
          }
        }
        enqueueSnackbar(t("Failed to create user"), { variant: "error" });
      });

    table.setCreatingRow(null); //exit creating mode
  };

  /** updates an user */
  const handleRowUpdate: MRT_TableOptions<UserManagementRow>["onEditingRowSave"] = async ({
    row,
    values,
    table
  }) => {
    const updated = updateUserCompanyDepartment(values, companyData, departmentData);

    const newValidationErrors = validateUserManagementRow(updated, row.original, t);

    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }
    setValidationErrors({});

    const newVal = mapUpdRowValueToUserDto(updated, row.original);

    await mutateUser([newVal])
      .then(() => enqueueSnackbar(t("UserInfoUpdateSuccess"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("UserInfoUpdateFail"), { variant: "error" }));
    table.setEditingRow(null); //exit editing mode
  };

  const column = useMemo<MRT_ColumnDef<UserManagementRow>[]>(() => {
    if (!userData || !companyData || !departmentData) return [];
    return [
      {
        accessorKey: "photoUrl",
        header: t("Avatar"),
        size: 56,
        minSize: 56,
        Cell: ({ row }) => userRowCellPhotoAvatar(row),
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        enableColumnActions: false
      },
      {
        accessorKey: "firstName",
        header: t("First Name"),
        size: 140,
        minSize: 130,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          type: "text",
          error: !!validationErrors?.firstName,
          helperText: validationErrors?.firstName,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              firstName: undefined
            }),
          onChange: e => handleRowInputChange(e, "firstName", validationErrors, setValidationErrors)
        }
      },
      {
        accessorKey: "surname",
        header: t("Last Name"),
        size: 140,
        minSize: 130,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          type: "text",
          error: !!validationErrors?.surname,
          helperText: validationErrors?.surname,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              surname: undefined
            }),
          onChange: e => handleRowInputChange(e, "surname", validationErrors, setValidationErrors)
        }
      },
      {
        accessorKey: "email",
        header: t("Email"),
        size: 250,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          type: "email",
          error: !!validationErrors?.email,
          helperText: validationErrors?.email,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              email: undefined
            }),
          onChange: e => handleRowInputChange(e, "email", validationErrors, setValidationErrors)
        }
      },
      {
        accessorKey: "supervisorInfo",
        header: t("Supervisor"),
        size: 250,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          type: "email",
          error: !!validationErrors?.supervisorInfo,
          helperText: validationErrors?.supervisorInfo,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              supervisorInfo: undefined
            }),
          onChange: e =>
            handleRowInputChange(e, "supervisorInfo", validationErrors, setValidationErrors)
        }
      },
      {
        accessorKey: "companyIdAndName",
        header: t("Company"),
        editSelectOptions: () =>
          companyData.map(d => ({ label: d.name, value: `${d.name}${d.id.toString()}` })),
        filterSelectOptions: companyData.map(d => ({
          label: d.name,
          value: `${d.name}${d.id.toString()}`
        })),
        size: 300,
        enableEditing: true,
        editVariant: "select",
        filterVariant: "multi-select",
        filterFn: "arrIncludesSome",
        Cell: ({ row }) => userRowCellCompanyId(row),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.companyId,
          helperText: validationErrors?.companyId,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              companyId: undefined
            }),
          onChange: e => handleRowInputChange(e, "companyId", validationErrors, setValidationErrors)
        }
      },
      {
        accessorKey: "departmentIdAndName",
        filterFn: "arrIncludesSome",
        editSelectOptions: [...nullDepartment, ...departmentData].map(d => ({
          label: d.name,
          value: `${d.name}${d.id.toString()}`
        })),
        filterSelectOptions: [...nullDepartment, ...departmentData].map(d => ({
          label: d.name,
          value: `${d.name}${d.id.toString()}`
        })),
        header: t("Department"),
        size: 300,
        enableEditing: true,
        editVariant: "select",
        filterVariant: "multi-select",
        Cell: ({ row }) => userRowCellDepartmentId(row)
      },
      {
        accessorKey: "isActive",
        header: t("Status"),
        size: 200,
        enableEditing: true,
        editVariant: "select",
        editSelectOptions: [
          { label: t("ACTIVE"), value: true },
          { label: t("INACTIVE"), value: false }
        ],
        filterVariant: "select",
        filterSelectOptions: [
          { label: t("ACTIVE"), value: true },
          { label: t("INACTIVE"), value: false }
        ],
        Cell: ({ row }) => userRowCellActive(row, t),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.isActive,
          helperText: validationErrors?.isActive,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              isActive: undefined
            }),
          onChange: e => handleRowInputChange(e, "isActive", validationErrors, setValidationErrors)
        }
      },
      {
        accessorKey: "isFirstAidUser",
        header: t("First Aid User"),
        size: 200,
        enableEditing: true,
        editVariant: "select",
        editSelectOptions: [
          { label: t("Yes"), value: true },
          { label: t("No"), value: false }
        ],
        filterVariant: "select",
        filterSelectOptions: [
          { label: t("Yes"), value: true },
          { label: t("No"), value: false }
        ],
        Cell: ({ row }) => userRowBooleanCell(row.original.isFirstAidUser, t)
      },
      {
        accessorKey: "participatesInMobileWorking",
        header: t("Mobile Working"),
        size: 200,
        enableEditing: true,
        editVariant: "select",
        editSelectOptions: [
          { label: t("Yes"), value: true },
          { label: t("No"), value: false }
        ],
        filterVariant: "select",
        filterSelectOptions: [
          { label: t("Yes"), value: true },
          { label: t("No"), value: false }
        ],
        Cell: ({ row }) => userRowBooleanCell(row.original.participatesInMobileWorking, t)
      },
      {
        accessorKey: "resetPassword",
        header: t("Reset Password"),
        size: 120,
        minSize: 120,
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        enableColumnActions: false,
        visibleInShowHideMenu: userRoles.isCompanyAdmin && userRoles.isTenantAdmin,
        Header: resetPasswordHeader(t),
        Cell: ({ row }) =>
          resetPasswordCell({
            row,
            resetPassword,
            isEdible: userRoles.isCompanyAdmin && userRoles.isTenantAdmin
          })
      }
    ];
  }, [userData, companyData, departmentData, validationErrors, t]);
  const seeditMrtOptions = useSeeditMrtTableOptions<UserManagementRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: users ?? [],
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: true,
    autoResetPageIndex: false,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    enableEditing: true,
    globalFilterFn: "contains",
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 },
      columnVisibility: {
        resetPassword: userRoles.isCompanyAdmin && userRoles.isTenantAdmin
      }
    }),
    state: {
      isLoading: isUserDataLoading,
      isSaving: (mutateUserStatus || deleteUserStatus) === "loading",
      showProgressBars: isUserDataFetching
    },
    getRowId: row => row.userId?.toString(),
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleRowAdd,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleRowUpdate,
    muiPaginationProps: {
      rowsPerPageOptions: [10, 15, 20],
      variant: "outlined"
    },
    positionToolbarAlertBanner: "none",
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("No users to display")
    },
    renderRowActions: ({ row, table }) => (
      <UserManagementTableRowActions
        row={row}
        table={table}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        setDeletionSelected={setDeletionSelected}
      />
    ),
    renderTopToolbar: ({ table }) => (
      <UserManagementTopToolbar
        table={table}
        disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
        onReload={async () => {
          await refetchUserData();
        }}
        onDelete={() => {
          setIsDeleteModalOpen(true);

          const selected = table.getSelectedRowModel().rows.map(row => row.original.email);
          setDeletionSelected(selected);
        }}
        onExportData={exportToExcel}
      />
    ),
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEditUserManagement
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        headline={t("Add User")}
      />
    ),
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEditUserManagement
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        headline={t("Edit User")}
      />
    )
  });

  const exportToExcel = async () => {
    const userbook = new ExcelJS.Workbook();

    const userInfo = userbook.addWorksheet("Upload");

    // define columns for user information
    userInfo.columns = [
      { header: "First_Name", key: "firstName", width: 15 },
      { header: "Last_Name", key: "lastName", width: 15 },
      { header: "Email", key: "email", width: 35 },
      { header: "Supervisor_Email", key: "supervisor", width: 35 },
      { header: "Company_Name", key: "company", width: 15 },
      { header: "Is_Active", key: "isActive", width: 10 },
      { header: "Department_Name", key: "department", width: 15 },
      // { header: t("Raum"), key: "raum", width: 10 },
      // { header: t("Location"), key: "location", width: 10 },
      { header: "First_Aid_User", key: "firstAidUser", width: 15 },
      { header: "Mobile_Working", key: "mobileWorking", width: 15 }
    ];

    // generate column data
    const userInfoData = users.map(user => ({
      firstName: user.firstName,
      lastName: user.surname,
      email: user.email,
      supervisor: user.supervisorInfo,
      company: user.companyName,
      isActive: user.isActive ? 1 : 0,
      department: user.departmentName ?? "",
      firstAidUser: user.isFirstAidUser ? t("Yes") : t("No"),
      mobileWorking: user.participatesInMobileWorking ? t("Yes") : t("No")
    }));

    // Add data rows to the sheet
    userInfoData.forEach(item => userInfo.addRow(item));

    // define columns for definition
    const definitionUser = userbook.addWorksheet("Definition");
    definitionUser.columns = [
      { header: "First_Name", key: "firstName", width: 15 },
      { header: "Last_Name", key: "lastName", width: 15 },
      { header: "Email", key: "email", width: 35 },
      { header: "Supervisor_Email", key: "supervisor", width: 35 },
      { header: "Company_Name", key: "company", width: 15 },
      { header: "Is_Active", key: "isActive", width: 10 },
      { header: "Department_Name", key: "department", width: 15 }
      // { header: t("Raum"), key: "raum", width: 10 },
      // { header: t("Location"), key: "location", width: 10 },
      // { header: t("First Aid User"), key: "firstAidUser", width: 15 },
      // { header: t("Mobile Working"), key: "mobileWorking", width: 15 }
    ];

    // static info of definition user properties
    const definitionUserData = [
      {
        firstName: t("_exportUserTableDescription_FIRST_NAME"),
        lastName: t("_exportUserTableDescription_LAST_NAME"),
        email: t("_exportUserTableDescription_EMAIL"),
        supervisor: t("_exportUserTableDescription_SUPERVISOR"),
        company: t("_exportUserTableDescription_COMPANY"),
        isActive: t("_exportUserTableDescription_IS_ACTIVE"),
        department: t("_exportUserTableDescription_DEPARTMENT")
      }
    ];

    // Add data rows to the sheet
    definitionUserData.forEach(item => definitionUser.addRow(item));

    // Create the Excel file buffer
    const buffer = await userbook.xlsx.writeBuffer();

    // language setting ?
    userbook.model.language = i18n.language;

    // Create a Blob from the buffer and download it
    const file = new Blob([buffer], { type: "application/octet-stream" });

    const url = URL.createObjectURL(file);
    // create element to save file
    exportExcelByDocument(url, t("Users"));

    URL.revokeObjectURL(url); // Cleanup the URL object after use
  };

  useEffect(() => {
    if (!userData) return;
    const useData = userData.map(d => ({
      ...d,
      companyIdAndName: `${d.companyName}${d.companyId.toString()}`,
      departmentIdAndName: `${d.departmentName ?? t("No Department")}${(d.departmentId ?? "-1").toString()}`,
      departmentName: d.departmentName ? d.departmentName : "",
      isFirstAidUser: d.isFirstAidUser ?? false,
      participatesInMobileWorking: d.participatesInMobileWorking ?? false
    }));

    setUsers(useData);
  }, [userData]);

  useEffect(() => {
    if (mutateUserStatus === "success") refetchUserData();
  }, [mutateUserStatus]);

  useEffect(() => {
    if (deleteUserStatus === "success") {
      setIsDeleteModalOpen(false);
      refetchUserData();
      setDeletionSelected(null);
    }
  }, [deleteUserStatus]);

  return (
    <Container
      style={{ maxWidth: "fit-content", padding: 0, margin: 0 }}
      component="main"
      data-testid="org-user-mgmt-container"
    >
      <div style={{ marginRight: 0, width: "min-content" }}>
        <InfoIconWithTooltip tooltipText={t("_userManagementInfo")} />
      </div>

      <Grid2 sx={{ marginBottom: "10px" }} container data-testid="org-user-mgmt-header">
        <Card sx={{ maxWidth: 300 }} variant="outlined">
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {t("Active users")}
            </Typography>
            <Typography variant="h5" component="h2">
              {users.filter(user => user.isActive).length}
            </Typography>

            <Typography variant="body2" component="p">
              {t("You can simply add or deactivate users")}
            </Typography>
          </CardContent>
        </Card>
      </Grid2>

      <MaterialReactTable table={table} />

      {/* Confirm Deletion Modal */}
      <TableDeleteConfirmDialog
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleUserDeletionConfirmationDesc")
            : t("_tableDeletionConfirmationDesc")
        }
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onConfirm={() => {
          /* istanbul ignore next */
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        deleteStatus={deleteUserStatus}
      />
    </Container>
  );
};
export default UserManagement;
